import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"
import { I18n } from 'i18n-js'
import locales from '../locales.json'

export default class extends Controller {
  static targets = ["output", "status", "input"]
  static values = {
    message: String,
    userToken: String,
    tenantId: String
  }

  static spinner = '<div id="custom-spinner" class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div>';

  connect() {
    console.log("AIChatController connected");

    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']").content

    this.buffer = ""; // Store received content incrementally

    if (this.messageValue == "") {
      console.error("No message value provided");
      return;
    }
    this.rendering = false; // Flag to prevent multiple renderings

    // Write the spinner before the first message in bold text
    this.appendContent(`### ${this.messageValue}`);

    // Connect to AWS WebSocket API
    console.log("Attempting to connect to WebSocket...");
    this.socket = new WebSocket("wss://ai.tshmanager.hu/production/");

    this.socket.onopen = () => {
      console.log("WebSocket connected");
      this.sendMessage({ action: "sendMessage", message: this.messageValue });
    };

    this.socket.onmessage = (event) => {
      console.log("WebSocket message received:", event.data);
      const data = JSON.parse(event.data);
      console.log("Parsed message content:", data.content);

      if (data.progress) {
        this.updateStatus(data.code, data.progress);
      }

      if (data.content) {
        // Remove spinner if present
        this.updateStatus("", false);
        this.appendContent(data.content);
      }
    };

    this.socket.onclose = (event) => {
      console.log(`WebSocket closed: ${event.code}, reason: ${event.reason}`);
    };

    this.socket.onerror = (error) => {
      console.error(`WebSocket error: ${error.message}`);
    };
  }

  send(event) {
    event.preventDefault();
    console.log("Send button clicked");
    const message = this.inputTarget.value;
    console.log("Sending message:", message);

    // Append bold message with spinner to content
    this.appendContent(`### ${message}`);

    this.sendMessage({ action: "sendMessage", message });
    this.inputTarget.value = ""; // Clear input field after sending
  }

  sendMessage(message) {
    console.log("Sending message:", message);
    const payload = {
      ...message,
      user_token: this.userTokenValue,
      tenant_id: this.tenantIdValue
    };
    this.socket.send(JSON.stringify(payload));
  }

  appendContent(content) {
    console.log("Appending content with typing effect:", content);
    let index = 0;

    const typeCharacter = () => {
      if (index < content.length) {
        this.buffer += content[index];
        this.outputTarget.innerHTML = marked.parse(this.buffer, { gfm: true, breaks: true });
        index++;
        setTimeout(typeCharacter, 25); // Adjust typing speed here (50ms per character)
      } else {
        this.buffer += "\n\n";
        this.outputTarget.innerHTML = marked.parse(this.buffer, { gfm: true, breaks: true });
        console.log("Typing effect completed");
      }
    };

    typeCharacter();
  }

  updateStatus(code, progress) {
    console.log("Updating status target with progress:", progress);
    this.statusTarget.classList.remove("d-none");

    if (code != "complete") {
      this.statusTarget.innerHTML = `
        <div style="display: flex; align-items: center;">
          ${this.constructor.spinner}
          <span style="margin-left: 10px;">${this.i18n.t('ai_chat.codes.' + code)}</span>
        </div>
      `;
    } else {
      this.statusTarget.innerHTML = `<span>${progress}</span>`;
      const spinnerElement = document.getElementById("custom-spinner");
      if (spinnerElement) {
        spinnerElement.remove();
      }
      this.statusTarget.classList.add("d-none");
    }
  }

  disconnect() {
    console.log("AIChatController disconnecting");
    if (this.socket) {
      console.log("Closing WebSocket connection");
      this.socket.close();
    }
  }
}
